// CANONICAL COPY (GH #7115). src/constants/frameworks/iso27001.js re-exports this file so the browser and the
// Function App share one compliance engine — one definition of "compliant". Edit here, never there.
'use strict';

const FRAMEWORK_ISO27001 = {
  id: 'fw-iso27001',
  type: 'standard',
  name: 'ISO 27001:2022',
  shortName: 'ISO 27001',
  abbrev: 'I27k1',
  description:
    'Information security management system requirements — 30 mandatory ISMS clauses across Clauses 4–10',
  color: '#60a5fa',
  version: '2022',
  sectionKeyDepth: 1,
  sections: [
    { key: '4', name: 'Context of the organization' },
    { key: '5', name: 'Leadership' },
    { key: '6', name: 'Planning' },
    { key: '7', name: 'Support' },
    { key: '8', name: 'Operation' },
    { key: '9', name: 'Performance evaluation' },
    { key: '10', name: 'Improvement' },
  ],
  mappings: [
    {
      externalId: '4.1',
      description:
        'Work out which internal and external issues affect the ISMS achieving its intended outcomes, and keep that understanding current.',
      scfId: 'SCF-MSS-001',
      ref: 'Clause 4.1',
      section: '4',
      title: 'Understanding the organization and its context',
    },
    {
      externalId: '4.2',
      description:
        'Identify the parties with an interest in information security, determine which of their requirements the ISMS must address, and keep that list current.',
      scfId: 'SCF-MSS-001',
      ref: 'Clause 4.2',
      section: '4',
      title: 'Understanding the needs and expectations of interested parties',
    },
    {
      externalId: '4.3',
      description:
        'Set and document the boundaries of the ISMS — which parts of the organisation, which locations, which activities — taking the context and interested parties into account.',
      scfId: 'SCF-MSS-001',
      ref: 'Clause 4.3',
      section: '4',
      title: 'Determining the scope of the ISMS',
    },
    {
      externalId: '4.4',
      description:
        'Establish the ISMS itself and keep it running: the processes it needs, how they interact, and their continual improvement.',
      scfId: 'SCF-MSS-001',
      ref: 'Clause 4.4',
      section: '4',
      title: 'Information security management system',
    },
    {
      externalId: '5.1',
      description:
        'Top management demonstrates leadership: resourcing the ISMS, integrating it into business processes, directing people, and holding it accountable for results.',
      scfId: 'SCF-GOV-004',
      ref: 'Clause 5.1',
      section: '5',
      title: 'Leadership and commitment',
    },
    {
      externalId: '5.2',
      description:
        'Establish an information security policy that fits the organisation, sets the frame for objectives, commits to requirements and improvement, and is communicated.',
      scfId: 'SCF-GOV-001',
      ref: 'Clause 5.2',
      section: '5',
      title: 'Policy',
    },
    {
      externalId: '5.3',
      description:
        'Assign and communicate responsibility and authority for the ISMS — who conforms it to the standard, and who reports on its performance.',
      scfId: 'SCF-GOV-002',
      ref: 'Clause 5.3',
      section: '5',
      title: 'Organizational roles, responsibilities and authorities',
    },
    {
      externalId: '6.1.1',
      description:
        'When planning the ISMS, consider the context and interested parties, and determine the risks and opportunities the ISMS has to address.',
      scfId: 'SCF-RSK-001',
      ref: 'Clause 6.1.1',
      section: '6.1',
      title: 'Actions to address risks and opportunities — General',
    },
    {
      externalId: '6.1.2',
      description:
        'Define and apply a repeatable information security risk assessment process that identifies, analyses and evaluates risk against stated criteria.',
      scfId: 'SCF-RSK-001',
      ref: 'Clause 6.1.2',
      section: '6.1',
      title: 'Information security risk assessment',
    },
    {
      externalId: '6.1.3',
      description:
        'Define and apply a risk treatment process: select options, determine the controls needed, compare them against Annex A, produce a Statement of Applicability, and get the plan and residual risk approved.',
      scfId: 'SCF-RSK-001',
      ref: 'Clause 6.1.3',
      section: '6.1',
      title: 'Information security risk treatment',
    },
    {
      externalId: '6.2',
      description:
        'Set information security objectives at relevant functions and levels, make them measurable and consistent with the policy, and plan how they will be achieved.',
      scfId: 'SCF-RSK-002',
      ref: 'Clause 6.2',
      section: '6',
      title: 'Information security objectives and planning to achieve them',
    },
    {
      externalId: '6.3',
      description:
        'When the ISMS itself needs to change, plan the change rather than making it ad hoc.',
      scfId: 'SCF-MSS-004',
      ref: 'Clause 6.3',
      section: '6',
      title: 'Planning of changes',
    },
    {
      externalId: '7.1',
      description:
        'Determine and provide the resources the ISMS needs to be established, maintained and improved.',
      scfId: 'SCF-MSS-002',
      ref: 'Clause 7.1',
      section: '7',
      title: 'Resources',
    },
    {
      externalId: '7.2',
      description:
        'Determine the competence required of people whose work affects information security, ensure they have it, and retain evidence.',
      scfId: 'SCF-PPL-003',
      ref: 'Clause 7.2',
      section: '7',
      title: 'Competence',
    },
    {
      externalId: '7.3',
      description:
        "Ensure people working under the organisation's control are aware of the policy, their contribution to the ISMS, and what not conforming would mean.",
      scfId: 'SCF-MSS-003',
      ref: 'Clause 7.3',
      section: '7',
      title: 'Awareness',
    },
    {
      externalId: '7.4',
      description:
        'Determine the internal and external communications the ISMS needs — what, when, with whom and how.',
      scfId: 'SCF-MSS-003',
      ref: 'Clause 7.4',
      section: '7',
      title: 'Communication',
    },
    {
      externalId: '7.5.1',
      description:
        'Hold the documented information the standard requires and whatever else the organisation decides it needs for the ISMS to be effective.',
      // Documented information, not policy. Clause 5.2 is the policy
      // requirement and correctly maps to SCF-GOV-001; pointing 7.5.1 there
      // too had a tenant's policy evidence answering a documented-information
      // clause. ISO 22301 7.5 and ISO 42001 7.5 — the same clause in the same
      // harmonised structure — already map here (GH #6844).
      scfId: 'SCF-GOV-011',
      ref: 'Clause 7.5.1',
      section: '7.5',
      title: 'Documented information — General',
    },
    {
      externalId: '7.5.2',
      description:
        'When creating or updating documented information, give it suitable identification, format and media, and review and approve it.',
      scfId: 'SCF-GOV-011',
      ref: 'Clause 7.5.2',
      section: '7.5',
      title: 'Creating and updating documented information',
    },
    {
      externalId: '7.5.3',
      description:
        'Control documented information so it is available where needed and adequately protected — distribution, access, retrieval, storage, version control and disposal.',
      scfId: 'SCF-DAT-001',
      ref: 'Clause 7.5.3',
      section: '7.5',
      title: 'Control of documented information',
    },
    {
      externalId: '8.1',
      description:
        'Plan, implement and control the processes that meet the information security requirements and carry out the risk treatment actions; control planned changes and review unintended ones.',
      scfId: 'SCF-MSS-004',
      ref: 'Clause 8.1',
      section: '8',
      title: 'Operational planning and control',
    },
    {
      externalId: '8.2',
      description:
        'Perform the risk assessment at planned intervals and whenever significant change is proposed or occurs, and retain the results.',
      scfId: 'SCF-RSK-001',
      ref: 'Clause 8.2',
      section: '8',
      title: 'Information security risk assessment',
    },
    {
      externalId: '8.3',
      description: 'Carry out the risk treatment plan and retain evidence of the results.',
      scfId: 'SCF-RSK-001',
      ref: 'Clause 8.3',
      section: '8',
      title: 'Information security risk treatment',
    },
    {
      externalId: '9.1',
      description:
        "Determine what to monitor and measure, by what methods, when, and by whom — and evaluate the ISMS's performance and effectiveness from it.",
      scfId: 'SCF-MSS-005',
      ref: 'Clause 9.1',
      section: '9',
      title: 'Monitoring, measurement, analysis and evaluation',
    },
    {
      externalId: '9.2.1',
      description:
        "Conduct internal audits at planned intervals to establish whether the ISMS conforms to the organisation's own requirements and to the standard, and is effectively implemented.",
      scfId: 'SCF-AUD-001',
      ref: 'Clause 9.2.1',
      section: '9.2',
      title: 'Internal audit — General',
    },
    {
      externalId: '9.2.2',
      description:
        'Plan, establish and maintain the audit programme itself: frequency, methods, responsibilities, reporting, and the criteria and scope of each audit.',
      scfId: 'SCF-AUD-001',
      ref: 'Clause 9.2.2',
      section: '9.2',
      title: 'Internal audit programme',
    },
    {
      externalId: '9.3.1',
      description:
        'Top management reviews the ISMS at planned intervals to confirm it remains suitable, adequate and effective.',
      scfId: 'SCF-MSS-006',
      ref: 'Clause 9.3.1',
      section: '9.3',
      title: 'Management review — General',
    },
    {
      externalId: '9.3.2',
      description:
        'The review considers defined inputs — the status of previous actions, changes in context, ISMS performance, feedback from interested parties, risk assessment results and improvement opportunities.',
      scfId: 'SCF-MSS-006',
      ref: 'Clause 9.3.2',
      section: '9.3',
      title: 'Management review inputs',
    },
    {
      externalId: '9.3.3',
      description:
        'The review produces decisions on continual improvement and any changes the ISMS needs, and the results are retained.',
      scfId: 'SCF-MSS-006',
      ref: 'Clause 9.3.3',
      section: '9.3',
      title: 'Management review results',
    },
    {
      externalId: '10.1',
      description: 'Continually improve the suitability, adequacy and effectiveness of the ISMS.',
      scfId: 'SCF-MSS-007',
      ref: 'Clause 10.1',
      section: '10',
      title: 'Continual improvement',
    },
    {
      externalId: '10.2',
      description:
        'React to nonconformities: control and correct them, deal with the consequences, evaluate whether the cause can be eliminated so they do not recur, and retain evidence of what was done.',
      scfId: 'SCF-MSS-007',
      ref: 'Clause 10.2',
      section: '10',
      title: 'Nonconformity and corrective action',
    },
  ],
};

module.exports = {
  FRAMEWORK_ISO27001,
};
